import {
  captureSyncV2LocalStateBeforeInitialPull,
  markSyncV2DomainsPending,
  pendingSyncV2PushOptions,
  prepareSyncPushRequest,
  pullSync,
  pushPreparedSyncRequest,
  pushSync,
  type PreparedSyncPushRequest,
  type PushSyncOptions,
} from "@/shared/api/sync";
import {
  captureAuthSessionOwner,
  fetchMe,
  isAuthSessionOwnerCurrent,
  isCurrentSessionLocalDataBootstrapAuthorityAudited,
  isCurrentSessionLocalDataBootstrapRequired,
  isSignedIn,
  markCurrentSessionLocalDataBootstrapComplete,
  type AuthSessionOwner,
} from "@/shared/auth/session";
import { onBriefProfileChanged, onBriefStreakChanged } from "@/shared/brief/run-store";
import { onHomeCarouselSettingsChanged } from "@/shared/home-carousel/carousel-store";
import { onMarketWatchlistChanged } from "@/shared/markets/watchlist";
import { onPreferencesChanged } from "@/shared/preferences/store";
import { onSavedArticlesChanged } from "@/shared/saved/store";
import { onTasksChanged } from "@/shared/tasks/store";
import { THEME_CHANGED_EVENT } from "@/shared/theme/theme-storage";
import { hasPendingSyncDeletions, pendingSyncDeletions } from "@/shared/sync/deletion-outbox";
import {
  acknowledgeCurrentAccountStorageBootstrapPendingPush,
  accountStorageScopeForStableId,
  completeCurrentAccountStorageBootstrap,
  ensureCurrentAccountStorageBootstrapPending,
  hasCurrentAccountStorageBootstrapPending,
  hasCurrentAccountStorageBootstrapServerCompleted,
  markCurrentAccountStorageBootstrapPushPending,
  markCurrentAccountStorageBootstrapServerCompleted,
  persistCurrentAccountStorageBootstrapRequest,
  readCurrentAccountStorageBootstrapPendingPushState,
  readCurrentAccountStorageBootstrapRequest,
} from "@/shared/storage/account-scope";
import { accountScopedSyncOptionsForStorageKey } from "./account-storage-sync-change.mjs";
import {
  createCloudSyncLifecycleController,
  type CloudSyncLifecycleFence,
} from "./cloud-sync-lifecycle.mjs";
import { createCloudReadDeadline } from "./cloud-read-deadline.mjs";
import {
  acknowledgeCurrentContentPushIntentSync,
  journalCurrentContentPushIntentSync,
  pendingCurrentContentPushOptions,
  readCurrentContentPushIntent,
  type ContentPushIntentReceipt,
} from "./content-push-intent";

const SYNC_STATUS_EVENT = "tb-public-web-cloud-sync-status";
const SYNC_DEBOUNCE_MS = 1_500;
const FOREGROUND_PULL_INTERVAL_MS = 45_000;
const ACCOUNT_IDENTITY_REFRESH_INTERVAL_MS = 45_000;
const PUSH_RETRY_BASE_MS = 2_000;
const PUSH_RETRY_MAX_MS = 60_000;
const PULL_RETRY_BASE_MS = 2_000;
const PULL_RETRY_MAX_MS = 60_000;
const AUTHORITY_RETRY_BASE_MS = 2_000;
const AUTHORITY_RETRY_MAX_MS = 60_000;
const FULL_ACCOUNT_BOOTSTRAP_OPTIONS: PushSyncOptions = {
  includePreferences: true,
  includeTheme: true,
  includeTasks: true,
  includeSavedArticles: true,
  includeWatchlist: true,
  includeStreak: true,
};

export type CloudSyncPhase = "idle" | "pulling" | "pushing" | "synced" | "offline" | "error";

export type CloudSyncStatus = {
  phase: CloudSyncPhase;
  detail?: string;
  lastSyncedAt?: string;
};

let status: CloudSyncStatus = { phase: "idle" };
let applyingRemote = false;
const lifecycleController = createCloudSyncLifecycleController();
const cloudReadDeadline = createCloudReadDeadline();
let activePullRunId: number | null = null;
let pullRunSequence = 0;
let activePullRun: {
  ownerKey: string;
  runId: number;
  controller: AbortController;
  promise: Promise<boolean>;
} | null = null;
let lastPullStartedAtEpochMs = 0;
let pendingOptions: PushSyncOptions = {};
let pendingOptionsOwnerKey: string | null = null;
let pushTimer: ReturnType<typeof setTimeout> | null = null;
let pushRetryAttempt = 0;
let pullRetryTimer: ReturnType<typeof setTimeout> | null = null;
let pullRetryAttempt = 0;
let foregroundPullTimer: ReturnType<typeof setInterval> | null = null;
let unsubscribeListeners: Array<() => void> = [];
let activeBootstrapRun: { ownerKey: string; runId: number } | null = null;
let bootstrapRunSequence = 0;
let activeAuthorityRun: {
  ownerKey: string;
  runId: number;
  controller: AbortController;
  promise: Promise<boolean>;
} | null = null;
let authorityRunSequence = 0;
let authorityRetryTimer: ReturnType<typeof setTimeout> | null = null;
let authorityRetryAttempt = 0;
let pendingInitialPullOwnerKey: string | null = null;
let hydratedOwnerKey: string | null = null;
const accountPullTails = new Map<string, Promise<void>>();
let activeInitialRun: { ownerKey: string; runId: number; promise: Promise<boolean> } | null = null;
let initialRunSequence = 0;
let lastIdentityRefreshStartedAtEpochMs = 0;
let activeIdentityRefresh: {
  ownerKey: string;
  controller: AbortController;
  promise: Promise<boolean>;
} | null = null;

function bootstrapOwnerKey(owner: AuthSessionOwner): string {
  return `${owner.userId.length}:${owner.userId}:${owner.sessionId}`;
}

function queueAccountPull<T>(owner: AuthSessionOwner, pull: () => Promise<T>): Promise<T> {
  const ownerKey = bootstrapOwnerKey(owner);
  const previous = accountPullTails.get(ownerKey) || Promise.resolve();
  const result = previous.then(pull);
  const tail = result.then(
    () => undefined,
    () => undefined,
  );
  accountPullTails.set(ownerKey, tail);
  void tail.then(() => {
    if (accountPullTails.get(ownerKey) === tail) accountPullTails.delete(ownerKey);
  });
  return result;
}

function captureLifecycleFence(owner: AuthSessionOwner): CloudSyncLifecycleFence | null {
  const ownerKey = bootstrapOwnerKey(owner);
  return lifecycleController.capture(ownerKey);
}

function isLifecycleFenceCurrent(
  fence: CloudSyncLifecycleFence | null,
  owner: AuthSessionOwner,
): boolean {
  return Boolean(
    lifecycleController.isActive(fence, bootstrapOwnerKey(owner)) &&
    isAuthSessionOwnerCurrent(owner),
  );
}

function isLifecycleFenceActive(fence: CloudSyncLifecycleFence | null): boolean {
  return lifecycleController.isActive(fence);
}

function lifecycleMutationSupersededError() {
  const error = new Error("auth_session_mutation_superseded") as Error & { code: string };
  error.code = "auth_session_mutation_superseded";
  return error;
}

function assertLifecycleFenceCurrent(
  fence: CloudSyncLifecycleFence | null,
  owner: AuthSessionOwner,
) {
  if (isLifecycleFenceCurrent(fence, owner)) return;
  throw lifecycleMutationSupersededError();
}

function hasAccountBootstrapWork(): boolean {
  return hasCurrentAccountStorageBootstrapPending() || isCurrentSessionLocalDataBootstrapRequired();
}

function ensureAccountBootstrapReady(): boolean {
  if (hasCurrentAccountStorageBootstrapPending()) return true;
  if (!isCurrentSessionLocalDataBootstrapRequired()) return false;
  return ensureCurrentAccountStorageBootstrapPending();
}

async function ensureBootstrapAuthorityAudited(owner: AuthSessionOwner): Promise<boolean> {
  const fence = captureLifecycleFence(owner);
  if (!isLifecycleFenceCurrent(fence, owner)) return false;
  if (isCurrentSessionLocalDataBootstrapAuthorityAudited()) return true;
  const ownerKey = bootstrapOwnerKey(owner);
  if (activeAuthorityRun?.ownerKey === ownerKey) return activeAuthorityRun.promise;

  const runId = ++authorityRunSequence;
  const controller = new AbortController();
  const promise = (async () => {
    emitStatus({ phase: "pulling", detail: "Checking your saved setup" });
    try {
      await cloudReadDeadline.run((signal) => fetchMe({ signal }), {
        signal: controller.signal,
      });
      if (
        !isLifecycleFenceCurrent(fence, owner) ||
        !isCurrentSessionLocalDataBootstrapAuthorityAudited()
      ) {
        return false;
      }
      authorityRetryAttempt = 0;
      lastIdentityRefreshStartedAtEpochMs = Date.now();
      return true;
    } catch (err) {
      if (mutationWasSuperseded(err) || !isLifecycleFenceCurrent(fence, owner)) return false;
      emitStatus({
        phase: errorPhase(err),
        detail: "Saved locally; account sync will retry.",
        lastSyncedAt: status.lastSyncedAt,
      });
      return false;
    } finally {
      if (activeAuthorityRun?.runId === runId) activeAuthorityRun = null;
    }
  })();
  activeAuthorityRun = { ownerKey, runId, controller, promise };
  return promise;
}

function emitStatus(next: CloudSyncStatus) {
  status = next;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SYNC_STATUS_EVENT, { detail: next }));
  }
}

function mergePushOptions(next: PushSyncOptions, owner: AuthSessionOwner) {
  const ownerKey = bootstrapOwnerKey(owner);
  if (!isAuthSessionOwnerCurrent(owner) || !lifecycleController.capture(ownerKey)) return;
  if (pendingOptionsOwnerKey && pendingOptionsOwnerKey !== ownerKey) {
    pendingOptions = {};
  }
  pendingOptionsOwnerKey = ownerKey;
  pendingOptions = {
    includePreferences: pendingOptions.includePreferences || next.includePreferences,
    includeTheme: pendingOptions.includeTheme || next.includeTheme,
    includeTasks: pendingOptions.includeTasks || next.includeTasks,
    includeSavedArticles: pendingOptions.includeSavedArticles || next.includeSavedArticles,
    includeWatchlist: pendingOptions.includeWatchlist || next.includeWatchlist,
    includeStreak: pendingOptions.includeStreak || next.includeStreak,
  };
}

function hasPendingPushOptions(owner: AuthSessionOwner): boolean {
  return (
    pendingOptionsOwnerKey === bootstrapOwnerKey(owner) &&
    Object.values(pendingOptions).some(Boolean)
  );
}

function readContentPushIntent(): ContentPushIntentReceipt {
  return readCurrentContentPushIntent();
}

function markContentPushIntent(opts: PushSyncOptions, owner: AuthSessionOwner): void {
  if ((!opts.includeTasks && !opts.includeSavedArticles) || !isAuthSessionOwnerCurrent(owner)) {
    return;
  }
  journalCurrentContentPushIntentSync(opts);
}

function pendingContentPushOptions(): PushSyncOptions {
  return pendingCurrentContentPushOptions();
}

function acknowledgeContentPushIntent(
  receipt: ContentPushIntentReceipt,
  pushedOptions: PushSyncOptions,
  owner: AuthSessionOwner,
): void {
  if (!isAuthSessionOwnerCurrent(owner)) return;
  acknowledgeCurrentContentPushIntentSync(receipt, pushedOptions);
}

function schedulePush(opts: PushSyncOptions = {}, expectedOwner: AuthSessionOwner | null = null) {
  const owner = expectedOwner || captureAuthSessionOwner();
  const fence = owner ? captureLifecycleFence(owner) : null;
  if (!owner || !isLifecycleFenceCurrent(fence, owner) || !isSignedIn() || applyingRemote) {
    return;
  }
  markContentPushIntent(opts, owner);
  markSyncV2DomainsPending(opts);
  mergePushOptions(opts, owner);
  const bootstrapWork = hasAccountBootstrapWork();
  if (bootstrapWork) markCurrentAccountStorageBootstrapPushPending(opts);
  const ownerKey = bootstrapOwnerKey(owner);
  if (
    pendingInitialPullOwnerKey === ownerKey ||
    !isCurrentSessionLocalDataBootstrapAuthorityAudited()
  ) {
    pendingInitialPullOwnerKey = ownerKey;
    void resumeInitialCloudSync(owner);
    return;
  }
  if (bootstrapWork) {
    void bootstrapNewAccount(owner).then(() => {
      if (isLifecycleFenceCurrent(fence, owner) && !hasAccountBootstrapWork()) {
        schedulePendingPushAfterInitialSync(owner, fence);
      }
    });
    return;
  }
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void flushCloudSync(owner, fence);
  }, SYNC_DEBOUNCE_MS);
}

function errorPhase(error: unknown): CloudSyncPhase {
  const code = String((error as { code?: unknown } | null)?.code || "");
  const name = String((error as { name?: unknown } | null)?.name || "");
  const message = String((error as Error)?.message || "");
  return /offline|network|failed to fetch|load failed|connection|timeout|abort/i.test(
    `${code} ${name} ${message}`,
  )
    ? "offline"
    : "error";
}

function mutationWasSuperseded(error: unknown): boolean {
  return (
    (error as { code?: unknown } | null)?.code === "auth_session_mutation_superseded" ||
    (error as { message?: unknown } | null)?.message === "auth_session_mutation_superseded"
  );
}

function pullCloudSync(owner: AuthSessionOwner): Promise<boolean> {
  const fence = captureLifecycleFence(owner);
  if (
    !isLifecycleFenceCurrent(fence, owner) ||
    !isSignedIn() ||
    !isCurrentSessionLocalDataBootstrapAuthorityAudited()
  ) {
    return Promise.resolve(false);
  }
  const ownerKey = bootstrapOwnerKey(owner);
  if (activePullRun?.ownerKey === ownerKey) return activePullRun.promise;

  const runId = ++pullRunSequence;
  const controller = new AbortController();
  activePullRunId = runId;
  lastPullStartedAtEpochMs = Date.now();
  const promise = (async () => {
    emitStatus({ phase: "pulling", detail: "Restoring your data" });
    let succeeded = false;
    try {
      await cloudReadDeadline.run(
        (signal) =>
          queueAccountPull(owner, () => {
            const pendingContentAtStart = pendingContentPushOptions();
            return pullSync({
              expectedOwner: owner,
              signal,
              assertCanApply: () => {
                if (activePullRunId !== runId) throw lifecycleMutationSupersededError();
                assertLifecycleFenceCurrent(fence, owner);
              },
              preserveLocalTasks: pendingContentAtStart.includeTasks === true,
              preserveLocalSavedArticles: pendingContentAtStart.includeSavedArticles === true,
              // Keep the network wait locally editable. Only suppress the synchronous
              // store events emitted while the fetched remote bundle is being applied.
              onBeforeApply: () => {
                if (activePullRunId === runId && isLifecycleFenceCurrent(fence, owner)) {
                  applyingRemote = true;
                }
              },
              onAfterApply: () => {
                if (activePullRunId === runId) applyingRemote = false;
              },
            });
          }),
        { signal: controller.signal },
      );
      if (!isLifecycleFenceCurrent(fence, owner)) return false;
      mergePushOptions(pendingSyncV2PushOptions(), owner);
      mergePushOptions(pendingContentPushOptions(), owner);
      succeeded = true;
      if (pullRetryTimer) {
        clearTimeout(pullRetryTimer);
        pullRetryTimer = null;
      }
      pullRetryAttempt = 0;
      hydratedOwnerKey = ownerKey;
      emitStatus({ phase: "synced", detail: "Synced", lastSyncedAt: new Date().toISOString() });
    } catch (err) {
      if (mutationWasSuperseded(err) || !isLifecycleFenceCurrent(fence, owner)) return false;
      emitStatus({
        phase: errorPhase(err),
        detail: "Sync will retry when your connection is available.",
        lastSyncedAt: status.lastSyncedAt,
      });
    } finally {
      if (activePullRunId === runId) {
        activePullRunId = null;
        applyingRemote = false;
      }
    }
    if (hasPendingSyncDeletions()) {
      const pending = pendingSyncDeletions();
      schedulePush(
        {
          includeTasks: pending.deletedTodos.length > 0,
          includeSavedArticles: pending.deletedSavedArticles.length > 0,
        },
        owner,
      );
    }
    return succeeded;
  })().finally(() => {
    if (activePullRun?.runId === runId) activePullRun = null;
  });
  activePullRun = { ownerKey, runId, controller, promise };
  return promise;
}

function durableBootstrapRequest(): PreparedSyncPushRequest {
  const existing = readCurrentAccountStorageBootstrapRequest();
  if (existing) return existing;
  const candidate = prepareSyncPushRequest(FULL_ACCOUNT_BOOTSTRAP_OPTIONS, {
    completeLocalDataBootstrap: true,
  });
  const persisted = persistCurrentAccountStorageBootstrapRequest(candidate);
  if (!persisted) {
    const error = new Error("account_storage_bootstrap_journal_failed") as Error & {
      code: string;
    };
    error.code = "account_storage_bootstrap_journal_failed";
    throw error;
  }
  return persisted;
}

async function bootstrapNewAccount(owner: AuthSessionOwner) {
  const ownerKey = bootstrapOwnerKey(owner);
  const fence = captureLifecycleFence(owner);
  if (
    activeBootstrapRun?.ownerKey === ownerKey ||
    !isLifecycleFenceCurrent(fence, owner) ||
    !hasAccountBootstrapWork()
  ) {
    return false;
  }
  if (!ensureAccountBootstrapReady()) {
    emitStatus({
      phase: "error",
      detail: "Saved locally; account sync will retry.",
      lastSyncedAt: status.lastSyncedAt,
    });
    return false;
  }
  const runId = ++bootstrapRunSequence;
  activeBootstrapRun = { ownerKey, runId };
  emitStatus({ phase: "pushing", detail: "Saving your current setup" });
  let completed = false;
  try {
    if (!hasCurrentAccountStorageBootstrapServerCompleted()) {
      const request = durableBootstrapRequest();
      // A legitimate response-loss retry replays as 200 through the stable
      // key/raw-body receipt. Any 409 remains a fail-closed mismatch: `/me`
      // alone cannot prove that this exact local snapshot was adopted.
      await pushPreparedSyncRequest(request, { expectedOwner: owner });
      if (!isLifecycleFenceCurrent(fence, owner)) return false;
      if (!markCurrentAccountStorageBootstrapServerCompleted()) {
        throw new Error("account_storage_bootstrap_server_receipt_not_journaled");
      }
    }

    // Post-response recovery is a strict local state machine:
    // server_completed journal -> durable session bit -> late-edit push ->
    // marker cleanup. A crash at any boundary resumes without re-uploading
    // the frozen bootstrap request or permitting an early pull.
    if (!markCurrentSessionLocalDataBootstrapComplete(owner)) {
      throw new Error("account_storage_bootstrap_session_not_persisted");
    }
    const latePush = readCurrentAccountStorageBootstrapPendingPushState();
    if (Object.values(latePush.options).some(Boolean)) {
      await pushSync(latePush.options, { expectedOwner: owner });
      if (
        !isLifecycleFenceCurrent(fence, owner) ||
        !acknowledgeCurrentAccountStorageBootstrapPendingPush(latePush.generation)
      ) {
        throw new Error("account_storage_bootstrap_late_edit_pending");
      }
    }
    const remainingLatePush = readCurrentAccountStorageBootstrapPendingPushState();
    if (Object.values(remainingLatePush.options).some(Boolean)) {
      throw new Error("account_storage_bootstrap_late_edit_pending");
    }
    if (!completeCurrentAccountStorageBootstrap()) {
      throw new Error("account_storage_bootstrap_cleanup_pending");
    }
    pendingOptions = {};
    pendingOptionsOwnerKey = null;
    // The durable bootstrap request and its optional late-edit request have
    // their own frozen receipts. Never read-and-clear the current content
    // token here: another tab may have written it during finalization.
    mergePushOptions(pendingContentPushOptions(), owner);
    completed = true;
    hydratedOwnerKey = ownerKey;
    emitStatus({
      phase: "synced",
      detail: "Your setup is saved",
      lastSyncedAt: new Date().toISOString(),
    });
  } catch (err) {
    if (mutationWasSuperseded(err) || !isLifecycleFenceCurrent(fence, owner)) return false;
    emitStatus({
      phase: errorPhase(err),
      detail: "Saved locally; account sync will retry.",
      lastSyncedAt: status.lastSyncedAt,
    });
  } finally {
    if (activeBootstrapRun?.runId === runId) {
      activeBootstrapRun = null;
    }
  }

  // A newly created account has no older remote state to restore. The full
  // push establishes the durable snapshot and v2 merge baseline; skipping the
  // usual initial pull also prevents a late local edit from being painted
  // back to defaults while the bootstrap request is in flight. Only a pull
  // may establish an incremental cursor.
  if (completed && hasPendingPushOptions(owner) && isLifecycleFenceCurrent(fence, owner)) {
    if (pushTimer) clearTimeout(pushTimer);
    pushTimer = setTimeout(() => {
      pushTimer = null;
      void flushCloudSync(owner, fence);
    }, SYNC_DEBOUNCE_MS);
  }
  return completed;
}

function scheduleInitialSyncRetry(
  owner: AuthSessionOwner,
  expectedFence: CloudSyncLifecycleFence | null = captureLifecycleFence(owner),
) {
  if (
    authorityRetryTimer ||
    !isLifecycleFenceCurrent(expectedFence, owner) ||
    pendingInitialPullOwnerKey !== bootstrapOwnerKey(owner)
  ) {
    return;
  }
  const delay = Math.min(
    AUTHORITY_RETRY_MAX_MS,
    AUTHORITY_RETRY_BASE_MS * 2 ** Math.min(authorityRetryAttempt, 5),
  );
  authorityRetryAttempt += 1;
  authorityRetryTimer = setTimeout(() => {
    authorityRetryTimer = null;
    if (isLifecycleFenceCurrent(expectedFence, owner)) {
      void resumeInitialCloudSync(owner);
    }
  }, delay);
}

function schedulePendingPushAfterInitialSync(
  owner: AuthSessionOwner,
  expectedFence: CloudSyncLifecycleFence | null = captureLifecycleFence(owner),
) {
  if (!isLifecycleFenceCurrent(expectedFence, owner) || !hasPendingPushOptions(owner)) {
    return;
  }
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void flushCloudSync(owner, expectedFence);
  }, SYNC_DEBOUNCE_MS);
}

function schedulePushRetry(
  owner: AuthSessionOwner,
  expectedFence: CloudSyncLifecycleFence | null = captureLifecycleFence(owner),
) {
  if (
    pushTimer ||
    !isLifecycleFenceCurrent(expectedFence, owner) ||
    !hasPendingPushOptions(owner) ||
    !isSignedIn()
  ) {
    return;
  }
  const delay = Math.min(
    PUSH_RETRY_MAX_MS,
    PUSH_RETRY_BASE_MS * 2 ** Math.min(pushRetryAttempt, 5),
  );
  pushRetryAttempt += 1;
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void flushCloudSync(owner, expectedFence);
  }, delay);
}

function schedulePullRetry(
  owner: AuthSessionOwner,
  expectedFence: CloudSyncLifecycleFence | null = captureLifecycleFence(owner),
) {
  if (
    pullRetryTimer ||
    !isLifecycleFenceCurrent(expectedFence, owner) ||
    !isSignedIn() ||
    !pageCanPullInForeground()
  ) {
    return;
  }
  const delay = Math.min(
    PULL_RETRY_MAX_MS,
    PULL_RETRY_BASE_MS * 2 ** Math.min(pullRetryAttempt, 5),
  );
  pullRetryAttempt += 1;
  pullRetryTimer = setTimeout(() => {
    pullRetryTimer = null;
    if (isLifecycleFenceCurrent(expectedFence, owner)) {
      void requestForegroundPull(owner, true);
    }
  }, delay);
}

function resumeInitialCloudSync(owner: AuthSessionOwner): Promise<boolean> {
  const ownerKey = bootstrapOwnerKey(owner);
  const fence = captureLifecycleFence(owner);
  if (!isLifecycleFenceCurrent(fence, owner) || pendingInitialPullOwnerKey !== ownerKey) {
    return Promise.resolve(false);
  }
  if (activeInitialRun?.ownerKey === ownerKey) return activeInitialRun.promise;

  const runId = ++initialRunSequence;
  const promise = (async () => {
    const authorityReady = await ensureBootstrapAuthorityAudited(owner);
    if (!authorityReady || !isLifecycleFenceCurrent(fence, owner)) {
      scheduleInitialSyncRetry(owner, fence);
      return false;
    }

    let completed = false;
    if (hasAccountBootstrapWork()) {
      completed = await bootstrapNewAccount(owner);
    } else {
      completed = await pullCloudSync(owner);
    }
    if (!completed || !isLifecycleFenceCurrent(fence, owner)) {
      scheduleInitialSyncRetry(owner, fence);
      return false;
    }

    pendingInitialPullOwnerKey = null;
    authorityRetryAttempt = 0;
    if (authorityRetryTimer) {
      clearTimeout(authorityRetryTimer);
      authorityRetryTimer = null;
    }
    schedulePendingPushAfterInitialSync(owner, fence);
    return true;
  })().finally(() => {
    if (activeInitialRun?.runId === runId) activeInitialRun = null;
  });
  activeInitialRun = { ownerKey, runId, promise };
  return promise;
}

function pageCanPullInForeground(): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (document.visibilityState !== "visible") return false;
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

function refreshAccountIdentity(owner: AuthSessionOwner): Promise<boolean> {
  const fence = captureLifecycleFence(owner);
  if (!isLifecycleFenceCurrent(fence, owner)) return Promise.resolve(false);
  const ownerKey = bootstrapOwnerKey(owner);
  if (activeIdentityRefresh?.ownerKey === ownerKey) {
    return activeIdentityRefresh.promise;
  }
  if (Date.now() - lastIdentityRefreshStartedAtEpochMs < ACCOUNT_IDENTITY_REFRESH_INTERVAL_MS) {
    return Promise.resolve(true);
  }
  lastIdentityRefreshStartedAtEpochMs = Date.now();
  const controller = new AbortController();
  const promise = cloudReadDeadline
    .run((signal) => fetchMe({ signal }), { signal: controller.signal })
    .then(() => isLifecycleFenceCurrent(fence, owner))
    .catch((error) => {
      if (mutationWasSuperseded(error) || !isLifecycleFenceCurrent(fence, owner)) {
        return false;
      }
      return false;
    })
    .finally(() => {
      if (activeIdentityRefresh?.promise === promise) activeIdentityRefresh = null;
    });
  activeIdentityRefresh = { ownerKey, controller, promise };
  return promise;
}

function requestForegroundPull(owner: AuthSessionOwner, force = false): Promise<boolean> {
  const ownerKey = bootstrapOwnerKey(owner);
  const fence = captureLifecycleFence(owner);
  if (
    !pageCanPullInForeground() ||
    !isLifecycleFenceCurrent(fence, owner) ||
    pendingInitialPullOwnerKey === ownerKey ||
    !isCurrentSessionLocalDataBootstrapAuthorityAudited() ||
    hasAccountBootstrapWork()
  ) {
    return Promise.resolve(false);
  }
  if (!force && Date.now() - lastPullStartedAtEpochMs < FOREGROUND_PULL_INTERVAL_MS) {
    return Promise.resolve(false);
  }
  return Promise.all([pullCloudSync(owner), refreshAccountIdentity(owner)]).then(([pulled]) => {
    if (pulled && isLifecycleFenceCurrent(fence, owner)) {
      schedulePendingPushAfterInitialSync(owner, fence);
    } else if (isLifecycleFenceCurrent(fence, owner)) {
      schedulePullRetry(owner, fence);
    }
    return pulled;
  });
}

export function refreshCloudSyncInForeground(): Promise<boolean> {
  const owner = captureAuthSessionOwner();
  if (!owner) return Promise.resolve(false);
  return requestForegroundPull(owner);
}

export async function flushCloudSync(
  expectedOwner: AuthSessionOwner | null = null,
  expectedFence: CloudSyncLifecycleFence | null = null,
) {
  const owner = expectedOwner || captureAuthSessionOwner();
  if (!owner || !isSignedIn()) return;
  const fence = expectedFence || captureLifecycleFence(owner);
  if (!isLifecycleFenceCurrent(fence, owner)) return;
  const ownerKey = bootstrapOwnerKey(owner);
  if (
    pendingInitialPullOwnerKey === ownerKey ||
    !isCurrentSessionLocalDataBootstrapAuthorityAudited()
  ) {
    pendingInitialPullOwnerKey = ownerKey;
    await resumeInitialCloudSync(owner);
    return;
  }
  if (hasAccountBootstrapWork()) {
    await bootstrapNewAccount(owner);
    return;
  }
  mergePushOptions(pendingContentPushOptions(), owner);
  if (!hasPendingPushOptions(owner)) return;
  const contentIntentReceipt = readContentPushIntent();
  const opts = pendingOptions;
  pendingOptions = {};
  pendingOptionsOwnerKey = null;
  emitStatus({ phase: "pushing", detail: "Saving changes", lastSyncedAt: status.lastSyncedAt });
  try {
    await pushSync(opts, { expectedOwner: owner });
    if (!isLifecycleFenceCurrent(fence, owner)) return;
    acknowledgeContentPushIntent(contentIntentReceipt, opts, owner);
    pushRetryAttempt = 0;
    emitStatus({ phase: "synced", detail: "Synced", lastSyncedAt: new Date().toISOString() });
  } catch (err) {
    if (mutationWasSuperseded(err) || !isLifecycleFenceCurrent(fence, owner)) return;
    mergePushOptions(opts, owner);
    const conflictCode = (err as { code?: unknown } | null)?.code;
    if (
      conflictCode === "sync_preference_conflict" ||
      conflictCode === "sync_theme_conflict" ||
      conflictCode === "sync_watchlist_conflict" ||
      conflictCode === "sync_streak_conflict" ||
      conflictCode === "sync_todo_rows_unacknowledged" ||
      conflictCode === "sync_saved_article_rows_unacknowledged"
    ) {
      const pulled = await pullCloudSync(owner);
      if (pulled && isLifecycleFenceCurrent(fence, owner)) {
        mergePushOptions(pendingSyncV2PushOptions(), owner);
        schedulePendingPushAfterInitialSync(owner, fence);
      } else {
        schedulePushRetry(owner, fence);
      }
      return;
    }
    emitStatus({
      phase: errorPhase(err),
      detail: "Saved locally; sync will retry.",
      lastSyncedAt: status.lastSyncedAt,
    });
    schedulePushRetry(owner, fence);
  }
}

function clearQueuedTaskPush(ownerKey: string): void {
  if (pendingOptionsOwnerKey !== ownerKey || !pendingOptions.includeTasks) return;
  pendingOptions = { ...pendingOptions, includeTasks: false };
  if (!Object.values(pendingOptions).some(Boolean) && pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
    pendingOptionsOwnerKey = null;
  }
}

/**
 * Push the Tasks snapshot for the direct provider coordinator while sharing
 * cloud-sync's durable receipt and in-memory queue. Claiming the matching
 * queued task bit prevents the normal debounced flush from racing an identical
 * account write against Google writeback; a newer token is never cleared.
 */
export async function pushTasksForConnectedProvider(options: {
  expectedOwner: AuthSessionOwner;
}): Promise<{ taskIntentAcknowledged: boolean }> {
  const owner = options.expectedOwner;
  const fence = captureLifecycleFence(owner);
  if (!isLifecycleFenceCurrent(fence, owner) || !isSignedIn()) {
    throw lifecycleMutationSupersededError();
  }
  const ownerKey = bootstrapOwnerKey(owner);
  const receipt = readContentPushIntent();
  if (receipt.tasksToken) clearQueuedTaskPush(ownerKey);

  try {
    await pushSync({ includeTasks: true }, { expectedOwner: owner });
  } catch (error) {
    if (isLifecycleFenceCurrent(fence, owner)) {
      mergePushOptions({ includeTasks: true }, owner);
      schedulePushRetry(owner, fence);
    }
    throw error;
  }
  if (!isLifecycleFenceCurrent(fence, owner)) {
    return { taskIntentAcknowledged: false };
  }

  try {
    const remaining = acknowledgeCurrentContentPushIntentSync(receipt, { includeTasks: true });
    const taskIntentAcknowledged =
      !receipt.tasksToken || remaining.tasksToken !== receipt.tasksToken;
    if (remaining.tasksToken) {
      mergePushOptions({ includeTasks: true }, owner);
      schedulePushRetry(owner, fence);
    } else {
      // Another direct coordinator may have re-queued this same domain while
      // our serialized account push was waiting. The durable receipt is the
      // authority: no remaining token means the duplicate can be cancelled.
      clearQueuedTaskPush(ownerKey);
    }
    return { taskIntentAcknowledged };
  } catch {
    mergePushOptions({ includeTasks: true }, owner);
    schedulePushRetry(owner, fence);
    return { taskIntentAcknowledged: false };
  }
}

/** Apply a provider-triggered account pull without echoing remote rows as edits. */
export function pullAccountSyncAfterProvider(options: {
  expectedOwner: AuthSessionOwner;
  assertCanApply: () => void;
}): Promise<unknown> {
  const owner = options.expectedOwner;
  const fence = captureLifecycleFence(owner);
  return queueAccountPull(owner, () => {
    assertLifecycleFenceCurrent(fence, owner);
    return cloudReadDeadline.run((signal) => {
      const pendingContentAtStart = pendingContentPushOptions();
      return pullSync({
        ...options,
        signal,
        assertCanApply: () => {
          assertLifecycleFenceCurrent(fence, owner);
          options.assertCanApply();
        },
        preserveLocalTasks: pendingContentAtStart.includeTasks === true,
        preserveLocalSavedArticles: pendingContentAtStart.includeSavedArticles === true,
        onBeforeApply: () => {
          applyingRemote = true;
        },
        onAfterApply: () => {
          applyingRemote = false;
        },
      });
    });
  }).then((result) => {
    if (isLifecycleFenceCurrent(fence, owner)) {
      mergePushOptions(pendingSyncV2PushOptions(), owner);
      mergePushOptions(pendingContentPushOptions(), owner);
      schedulePendingPushAfterInitialSync(owner, fence);
    }
    return result;
  });
}

function installListeners(owner: AuthSessionOwner) {
  cleanupCloudSync();
  lifecycleController.activate(bootstrapOwnerKey(owner));
  if (typeof window === "undefined") return;
  unsubscribeListeners = [
    onPreferencesChanged(() => schedulePush({ includePreferences: true }, owner)),
    onTasksChanged(() => {
      // Explicit local task mutations journal their durable intent before the
      // row/event write. Provider/account pulls intentionally do not; treating
      // those remote applies as new local edits would fence the next inbound
      // pass and bounce the same snapshot back to the server.
      if (pendingCurrentContentPushOptions().includeTasks === true) {
        schedulePush({ includeTasks: true }, owner);
      }
    }),
    onSavedArticlesChanged(() => schedulePush({ includeSavedArticles: true }, owner)),
    onMarketWatchlistChanged(() =>
      schedulePush({ includeWatchlist: true, includePreferences: true }, owner),
    ),
    onBriefProfileChanged(() => schedulePush({ includePreferences: true }, owner)),
    onBriefStreakChanged(() => schedulePush({ includeStreak: true }, owner)),
    onHomeCarouselSettingsChanged(() => schedulePush({ includePreferences: true }, owner)),
  ];
  const onThemeChanged = () => schedulePush({ includeTheme: true }, owner);
  const accountScope = accountStorageScopeForStableId(owner.userId);
  const onAccountStorageChanged = (event: StorageEvent) => {
    if (
      !accountScope ||
      !isAuthSessionOwnerCurrent(owner) ||
      (event.storageArea && event.storageArea !== window.localStorage)
    ) {
      return;
    }
    const options = accountScopedSyncOptionsForStorageKey(event.key, accountScope);
    if (options) schedulePush(options);
  };
  const onForeground = () => {
    if (!isAuthSessionOwnerCurrent(owner)) return;
    void requestForegroundPull(owner);
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") onForeground();
  };
  const onOnline = () => {
    const fence = captureLifecycleFence(owner);
    if (!isLifecycleFenceCurrent(fence, owner)) return;
    const ownerKey = bootstrapOwnerKey(owner);
    if (
      pendingInitialPullOwnerKey === ownerKey ||
      !isCurrentSessionLocalDataBootstrapAuthorityAudited()
    ) {
      pendingInitialPullOwnerKey = ownerKey;
      void resumeInitialCloudSync(owner);
      return;
    }
    if (hasAccountBootstrapWork()) {
      void bootstrapNewAccount(owner);
      return;
    }
    const deletions = pendingSyncDeletions();
    if (deletions.deletedTodos.length || deletions.deletedSavedArticles.length) {
      mergePushOptions(
        {
          includeTasks: deletions.deletedTodos.length > 0,
          includeSavedArticles: deletions.deletedSavedArticles.length > 0,
        },
        owner,
      );
    }
    mergePushOptions(pendingSyncV2PushOptions(), owner);
    mergePushOptions(pendingContentPushOptions(), owner);
    if (hasPendingPushOptions(owner)) {
      if (pushTimer) {
        clearTimeout(pushTimer);
        pushTimer = null;
      }
      void flushCloudSync(owner, fence).finally(() => {
        if (isLifecycleFenceCurrent(fence, owner)) {
          void requestForegroundPull(owner, true);
        }
      });
      return;
    }
    void requestForegroundPull(owner, true);
  };
  window.addEventListener(THEME_CHANGED_EVENT, onThemeChanged);
  window.addEventListener("storage", onAccountStorageChanged);
  window.addEventListener("focus", onForeground);
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisibilityChange);
  foregroundPullTimer = setInterval(() => {
    void requestForegroundPull(owner);
  }, FOREGROUND_PULL_INTERVAL_MS);
  unsubscribeListeners.push(() => window.removeEventListener(THEME_CHANGED_EVENT, onThemeChanged));
  unsubscribeListeners.push(() => window.removeEventListener("storage", onAccountStorageChanged));
  unsubscribeListeners.push(() => window.removeEventListener("focus", onForeground));
  unsubscribeListeners.push(() => window.removeEventListener("online", onOnline));
  unsubscribeListeners.push(() =>
    document.removeEventListener("visibilitychange", onVisibilityChange),
  );
}

export function startCloudSync(): () => void {
  if (!isSignedIn()) {
    cleanupCloudSync();
    emitStatus({ phase: "idle" });
    return cleanupCloudSync;
  }
  const owner = captureAuthSessionOwner();
  if (!owner) return cleanupCloudSync;
  captureSyncV2LocalStateBeforeInitialPull();
  installListeners(owner);
  mergePushOptions(pendingContentPushOptions(), owner);
  mergePushOptions({ includeStreak: true }, owner);
  pendingInitialPullOwnerKey = bootstrapOwnerKey(owner);
  void resumeInitialCloudSync(owner);
  const fence = captureLifecycleFence(owner);
  return () => {
    if (isLifecycleFenceActive(fence)) cleanupCloudSync();
  };
}

export function cleanupCloudSync() {
  lifecycleController.invalidate();
  const superseded = lifecycleMutationSupersededError();
  activePullRun?.controller.abort(superseded);
  activeAuthorityRun?.controller.abort(superseded);
  activeIdentityRefresh?.controller.abort(superseded);
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  if (pullRetryTimer) {
    clearTimeout(pullRetryTimer);
    pullRetryTimer = null;
  }
  if (authorityRetryTimer) {
    clearTimeout(authorityRetryTimer);
    authorityRetryTimer = null;
  }
  if (foregroundPullTimer) {
    clearInterval(foregroundPullTimer);
    foregroundPullTimer = null;
  }
  unsubscribeListeners.forEach((unsubscribe) => unsubscribe());
  unsubscribeListeners = [];
  pendingOptions = {};
  pendingOptionsOwnerKey = null;
  pushRetryAttempt = 0;
  pullRetryAttempt = 0;
  applyingRemote = false;
  activePullRunId = null;
  activePullRun = null;
  lastPullStartedAtEpochMs = 0;
  activeBootstrapRun = null;
  activeAuthorityRun = null;
  activeInitialRun = null;
  pendingInitialPullOwnerKey = null;
  hydratedOwnerKey = null;
  authorityRetryAttempt = 0;
  activeIdentityRefresh = null;
  lastIdentityRefreshStartedAtEpochMs = 0;
}

export function getCloudSyncStatus(): CloudSyncStatus {
  return status;
}

/** Owner/lifecycle-scoped readiness for provider pulls after initial hydration. */
export function isCurrentAccountCloudHydrated(): boolean {
  const owner = captureAuthSessionOwner();
  if (!owner || hydratedOwnerKey !== bootstrapOwnerKey(owner)) return false;
  return Boolean(
    isLifecycleFenceCurrent(captureLifecycleFence(owner), owner) &&
    pendingInitialPullOwnerKey !== bootstrapOwnerKey(owner) &&
    isCurrentSessionLocalDataBootstrapAuthorityAudited() &&
    !hasAccountBootstrapWork(),
  );
}

export function onCloudSyncStatusChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(SYNC_STATUS_EVENT, listener);
  return () => window.removeEventListener(SYNC_STATUS_EVENT, listener);
}
